<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Vue
    import { onMounted, reactive, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';
    import { inject } from 'vue';

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const commonStore = useCommonStore();
    const reserveStore = useReserveStore();
    const languageStore = useLanguageStore();
    const from = commonStore.from;
    const app = getCurrentInstance();

    // メッセージダイアログ取得
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const requestData = reactive(reserveStore.rsvdtlRequest);
    const checked = ref(false);

    // ----------------------------
    // 関数
    // ----------------------------
    // 戻るボタン押下時
    function onBack() {
        // 前画面からの戻るフラグを有効にする。
        commonStore.reservePageBack = true;

        // 遷移元予約画面のルートと、設定・選択状況に応じて、戻る画面を制御する。
        switch (commonStore.from) {
            case 'consultation':
                // 相談内容を選んで予約のルートは対象外

                // 予約(相談内容からサービス選択)画面
                if (reserveStore.reserveStep.consultation.reserveplanningservice) {
                    router.push({ name: 'reserveplanningservice' });
                    return;
                }

                // 予約(相談内容から利用方法等選択)画面
                if (reserveStore.reserveStep.consultation.reserveplanningconsultation) {
                    router.push({ name: 'reserveplanningconsultation' });
                    return;
                }
                break;
            case 'program':
                // プログラムを選んで予約のルート

                // 予約(プログラムからサービス選択)画面
                if (reserveStore.reserveStep.program.reserveplanningprogram) {
                    router.push({ name: 'reserveplanningprogram' });
                    return;
                }
                break;
            case 'personal':
                // 割当てられたプログラムを予約のルート

                // 予約(プログラムからサービス選択)画面
                if (reserveStore.reserveStep.personal.reserveplanningpersonal) {
                    router.push({ name: 'reserveplanningpersonal' });
                    return;
                }
                break;
            default:
                // 戻り先指定無し。
                break;
        }
    }
    // 進むボタン押下時
    function onAdvance() {
        // 同意した規約IDを保持する。
        requestData.RegulationHistPk = reserveStore.regulationDetail.RegulationHistPk;

        // データを格納する。
        reserveStore.setRsvdtlRequestData(requestData);

        // 前画面に応じて画面遷移先が変化
        switch (from) {
            case 'consultation':
                // (consultation画面)予約(相談内容から利用方法等選択)画面からのルート
                router.push('/reserve/planning/counselor');
                break;
            default:
                // (program画面)予約(プログラムからサービス選択)画面からのルート
                // (personal画面)予約(プログラムから利用方法など選択)画面からのルート
                router.push('/reserve/planning/selection');
                break;
        }
    }
    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // 前画面から戻ってきたか判定する。
        if (commonStore.reservePageBack) {
            // 前画面から遷移してきた場合は、同意のチェックボックスを有効にする。
            checked.value = true;
        }

        // 前画面からの戻るフラグを初期化する。
        commonStore.reservePageBack = false;

        // 戻り先座標を記録する。
        // 遷移元予約画面のルートに応じて、戻る画面を制御する。
        switch (commonStore.from) {
            case 'consultation':
                // 相談内容を選んで予約のルート

                // 予約(規定同意)画面として記録する。
                reserveStore.reserveStep.consultation.reserveplanningagree = true;
                break;
            case 'program':
                // プログラムを選んで予約のルート

                // 予約(規定同意)画面として記録する。
                reserveStore.reserveStep.program.reserveplanningagree = true;
                break;
            case 'personal':
                // 割当てられたプログラムを予約のルート

                // 予約(規定同意)画面として記録する。
                reserveStore.reserveStep.personal.reserveplanningagree = true;
                break;
            default:
                // 何もしない。
                break;
        }

        // Storeを初期化する。
        reserveStore.clearRegulationDetail();

        // 規定詳細取得APIのクエリパラメーターを生成する。
        const regulationRequest = {
            Lang: languageStore.getLanguageValue,
            RegulationPk: requestData.RegulationPk,
        };

        // 規定詳細取得APIをコールする。
        const response = await reserveStore.getRegulationDetailAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, regulationRequest);

        // エラーがある場合は、エラー処理を行い処理を終了する。
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    });
</script>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reservePlanningAgree.parts.pageTitle1') }}</span>
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step3.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step2.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step2.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step3_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step2_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step2_en.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                </h3>
                <section class="whiteBoxContents">
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step3.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step2.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step2.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step3_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step2_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step2_en.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <p class="pageInfo">{{ $t('reservePlanningAgree.parts.pageInformation1') }}</p>
                    <div class="regulationBox">
                        <span class="regulationDate">{{ reserveStore.regulationDetail.RegulationDt }}</span>
                        <span class="regulationTitle">{{ reserveStore.regulationDetail.RegulationNm }}</span>
                        <div class="regulationBody">
                            {{ reserveStore.regulationDetail.RegulationContent }}
                        </div>
                    </div>
                    <ul class="btnArea center">
                        <li>
                            <!--規約同意チェックボックス-->
                            <input type="checkbox" id="checkbox1" class="checkbox" v-model="checked" />
                            <label for="checkbox1" class="checkbox label">{{ $t('reservePlanningAgree.parts.checkboxLabel1') }}</label>
                        </li>
                    </ul>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--戻る-->
                            <button type="button" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <!--進む-->
                            <button type="submit" class="btnPrimary" @click="onAdvance" :disabled="!checked">{{ $t('common.parts.nextPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
